type EditorNode = {
	type?: string;
	text?: string;
	content?: EditorNode[];
};

// Any whitespace or zero-width / invisible character. A message body counts as
// present only if it has at least one visible character — leading/trailing
// whitespace never matters. `\s` covers regular whitespace (incl. U+00A0/U+FEFF);
// the rest are zero-width chars TipTap/ProseMirror can leave around inline atoms
// (recipient/amount/hashtag pills) that `String.trim()` does not strip.
// Alternation (not a character class) avoids the misleading-character-class lint
// on the zero-width joiners.
const BLANK_CHARS = /\s|\u200B|\u200C|\u200D|\u2060|\uFEFF/g;

/** True when `text` has no visible characters (only whitespace / zero-width). */
const isBlankText = (text: string): boolean => text.replace(BLANK_CHARS, '').length === 0;

/**
 * Deep-checks a TipTap doc (or any node) for a text node with visible content.
 * Used to answer "does this editor content include a message body?" without
 * mounting the editor — e.g. give-box draft detection and start-over attribution.
 */
export const hasNonWhitespaceTextNode = (node: unknown): boolean => {
	if (!node || typeof node !== 'object') {
		return false;
	}

	const editorNode = node as EditorNode;
	if (editorNode.type === 'text' && typeof editorNode.text === 'string' && !isBlankText(editorNode.text)) {
		return true;
	}

	if (!Array.isArray(editorNode.content)) {
		return false;
	}

	return editorNode.content.some(hasNonWhitespaceTextNode);
};

const hasNonWhitespaceString = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

/**
 * True when a `GiveBoxEvent.Open` payload carries a message body (not just
 * recipients / amount / hashtags). The open event is the single source of
 * truth for whether Bizy handed off message content, so give-box attribution
 * seeds its start-over detection from this rather than sniffing the editor
 * after mount (which is timing-sensitive on slow TipTap inits). See BIZY-666.
 */
export const openEventHasMessageContent = (data?: { aiDraft?: string; text?: string; editorContent?: unknown }): boolean => {
	if (!data) return false;
	return hasNonWhitespaceString(data.aiDraft) || hasNonWhitespaceString(data.text) || hasNonWhitespaceTextNode(data.editorContent);
};
